import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { CommentContext, ContentReferenceContext } from '@gitbook/app-database';
import type { NavigationCommentsSideSheet } from '@gitbook/app-navigation';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveClient, useHiveRevision } from '@gitbook/hive-react';
import { createRadioPalette, PaletteButton, SidePanelSheet } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    BreadcrumbButton,
    BreadcrumbItem,
    BreadcrumbSpan,
    Breadcrumbs,
    ButtonGroup,
    Description,
    EmptyState,
    EmptyStateIcon,
    Heading,
    Pagination,
    Tooltip,
} from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';
import { assertNever } from '@gitbook/util-ts';

import { apiQuery } from '../../state';
import { APIErrorBoundary } from '../APIErrorBoundary';
import { ContentReferenceContextProvider } from '../ContentReferences';
import { formatUserPrettyName } from '../Users';
import { CommentCard } from './CommentCard';
import { CommentBoxSkeleton } from './CommentSkeleton';
import { CommentStatusFilters, FilterCommentsButton } from './FilterCommentsButton';
import { PostCommentBox } from './PostCommentBox';
import { useListPaginatedComments } from './useListPaginatedComments';
import { isCommentOnDeletedNodeOrPage } from './utils';

const COMMENT_SKELETON_COUNT = 3;

enum CommentsSidePanelTabs {
    All = 'all',
    Page = 'page',
}

const copy = {
    space: 'Leave comments on the content, or use the editor to comment on a specific block.',
    cr: 'Describe the edits in this change request and use @someone to collaborate.',
};

export type CommentsSidePanelState = {
    currentPage: string | null;
    sideSheet: NavigationCommentsSideSheet;
};

/**
 * SideSheet to display the comments on a space.
 */
export function CommentsSidePanel(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    analyticsContext: analytics.AnalyticsContextSpaceContent;

    /** Current state of the side panel to display */
    state: CommentsSidePanelState;

    /** Callback to navigate to a new state */
    onNavigate: (state: CommentsSidePanelState) => void;

    /** Callback to close the side panel */
    onClose: () => void;
}): React.ReactElement {
    const { space, changeRequest, state, onNavigate, onClose } = props;

    const { revision } = useHiveRevision();
    const currentPage =
        state.currentPage &&
        revision.pages[state.currentPage] &&
        hiveCore.isRevisionPageDocument(revision.pages[state.currentPage])
            ? (revision.pages[state.currentPage] as hiveCore.RevisionPageDocument)
            : null;

    const { sideSheet } = state;

    const [selectedTab, setSelectedTab] = useStateWithDeps<CommentsSidePanelTabs>(
        currentPage ? CommentsSidePanelTabs.Page : CommentsSidePanelTabs.All,
        [state.sideSheet.tab]
    );

    const setOpenedComment = (comment: api.Comment | undefined) => {
        onNavigate({
            currentPage: comment?.target.page?.id || null,
            sideSheet: {
                type: 'comments',
                node: comment?.target.node?.id || undefined,
                ...(comment?.id ? { comment: comment.id } : {}),
                ...(comment?.target.tableCell
                    ? {
                          tableCell: {
                              record: comment.target.tableCell.record,
                              definition: comment.target.tableCell.definition,
                          },
                      }
                    : {}),
            },
        });
    };

    const contentReferenceContext: ContentReferenceContext = {
        organization: space.organization,
        space: space.id,
        page: currentPage?.id,
        changeRequest: changeRequest?.id,
    };

    return (
        <ContentReferenceContextProvider value={contentReferenceContext}>
            <SidePanelSheet title="Comments" expandable onClose={onClose}>
                {sideSheet.comment ? (
                    <OpenedCommentBody
                        space={space}
                        changeRequest={changeRequest}
                        openedCommentId={sideSheet.comment}
                        showContextLink={selectedTab === CommentsSidePanelTabs.All}
                        onGoToAllComments={() => setOpenedComment(undefined)}
                    />
                ) : (
                    <ListCommentsBody
                        space={space}
                        changeRequest={changeRequest}
                        currentPage={currentPage}
                        sideSheet={sideSheet}
                        selectedTab={selectedTab}
                        setSelectedTab={setSelectedTab}
                        onOpenComment={setOpenedComment}
                    />
                )}
            </SidePanelSheet>
        </ContentReferenceContextProvider>
    );
}

function ListCommentsBody(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    currentPage: hiveCore.RevisionPageDocument | null;
    sideSheet: NavigationCommentsSideSheet;
    selectedTab: CommentsSidePanelTabs;
    setSelectedTab: (tab: CommentsSidePanelTabs) => void;
    onOpenComment: (comment: api.Comment) => void;
}) {
    const {
        space,
        changeRequest,
        currentPage,
        selectedTab,
        setSelectedTab,
        sideSheet,
        onOpenComment,
    } = props;

    const [statusFilter, setStatusFilter] = React.useState<CommentStatusFilters>(
        CommentStatusFilters.Open
    );
    const [authorsFilter, setAuthorsFilter] = React.useState<Set<string>>(new Set());

    // Fetches all comments, filtered by page, status and author.
    const [comments, next, previous] = useListPaginatedComments({
        spaceId: space.id,
        changeRequestId: changeRequest?.id || undefined,
        pageId:
            selectedTab === CommentsSidePanelTabs.Page && currentPage?.id
                ? currentPage.id
                : undefined,
        status: statusFilter,
        authors: authorsFilter,
    });

    /* TODO: for now we show the different sidepanel views in a palette, not tabs. Tabs didn't fit well with the isolated-view for comments. We should revisit this as we should make this a SidePanel config thing and not just an extra div in the body here. */
    const getCommentViewsPalette = React.useCallback(() => {
        return createRadioPalette({
            value: selectedTab,
            options: [
                {
                    value: CommentsSidePanelTabs.Page,
                    title: 'This page',
                },
                {
                    value: CommentsSidePanelTabs.All,
                    title: 'All pages',
                },
            ],
            onSelect: (tab) => setSelectedTab(tab),
        });
    }, [selectedTab, setSelectedTab]);

    /**
     * The context in which new comments will be created in. It depends on how the
     * sidesheet was opened.
     */
    const postCommentContext: CommentContext = React.useMemo(
        () => ({
            space: space.id,
            changeRequest: changeRequest?.id || null,
            page: currentPage?.id,

            // If a node is selected and we were in the context of a new post, add the node and the sheet to the context
            ...(sideSheet.node && sideSheet.context === 'post'
                ? {
                      node: sideSheet.node,
                      ...(sideSheet.tableCell
                          ? {
                                tableCell: {
                                    record: sideSheet.tableCell.record,
                                    definition: sideSheet.tableCell.definition,
                                },
                            }
                          : {}),
                  }
                : {}),
        }),
        [space.id, changeRequest?.id, sideSheet, currentPage?.id]
    );

    return (
        <>
            <div className="w-full border-base border-b bg-base p-2">
                <ButtonGroup>
                    {currentPage ? (
                        <PaletteButton
                            size="small"
                            className="w-fit"
                            key="comment-view"
                            kind="blank"
                            label={
                                selectedTab === CommentsSidePanelTabs.Page
                                    ? 'This page'
                                    : 'All pages'
                            }
                            palette={() => getCommentViewsPalette()}
                            trailingIcon={Icon.ChevronsVertical}
                        />
                    ) : null}
                    <FilterCommentsButton
                        spaceId={space.id}
                        changeRequestId={changeRequest?.id}
                        statusFilter={statusFilter}
                        setStatusFilter={setStatusFilter}
                        authorsFilter={authorsFilter}
                        setAuthorsFilter={setAuthorsFilter}
                    />
                </ButtonGroup>
            </div>
            {/* Ensure the container is `relative` so that any focus in this container remains trapped within it and doesn't cause the entire document to shift and offset */}
            <div className="scrollbar relative flex flex-1 flex-col overflow-y-auto">
                <React.Suspense
                    fallback={
                        <div className="flex h-full w-full flex-col justify-start p-2">
                            {Array.from({ length: COMMENT_SKELETON_COUNT }, (_, i) => (
                                <CommentBoxSkeleton key={i} />
                            ))}
                        </div>
                    }
                >
                    <div className="flex w-full flex-1 flex-col gap-2.5 bg-base">
                        {comments.length === 0 ? (
                            <CommentsEmptyState
                                selectedTab={selectedTab}
                                isInChangeRequest={!!changeRequest}
                            />
                        ) : (
                            <CommentsList
                                space={space}
                                changeRequest={changeRequest}
                                comments={comments}
                                onOpenComment={onOpenComment}
                                next={next}
                                previous={previous}
                                selectedTab={selectedTab}
                            />
                        )}
                    </div>
                </React.Suspense>
            </div>
            {currentPage ? (
                <PostCommentBox currentPage={currentPage} context={postCommentContext} />
            ) : null}
        </>
    );
}

function OpenedCommentBody(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    openedCommentId: string;
    showContextLink: boolean;
    onGoToAllComments: () => void;
}) {
    const { space, changeRequest, openedCommentId, showContextLink, onGoToAllComments } = props;

    const { data: openedComment } = useSuspenseQuery(
        changeRequest
            ? apiQuery(
                  'getCommentInChangeRequest',
                  {
                      spaceId: space.id,
                      changeRequestId: changeRequest.id,
                      commentId: openedCommentId,
                  },
                  { nullOnError: true }
              )
            : apiQuery(
                  'getCommentInSpace',
                  {
                      spaceId: space.id,
                      commentId: openedCommentId,
                  },
                  { nullOnError: true }
              )
    );

    return (
        <>
            <div className="w-full border-base border-b bg-base p-2">
                <Breadcrumbs label="Comments breadcrumbs">
                    <BreadcrumbItem>
                        <BreadcrumbButton
                            key="breadcrumb-back-to-comments"
                            isActive={false}
                            onPress={() => onGoToAllComments()}
                            className="w-fit"
                        >
                            All
                        </BreadcrumbButton>
                    </BreadcrumbItem>
                    {openedComment ? (
                        <BreadcrumbItem>
                            <BreadcrumbSpan isCurrent>
                                Comment from{' '}
                                {openedComment.postedBy?.displayName
                                    ? formatUserPrettyName(openedComment.postedBy.displayName)
                                    : 'someone'}
                            </BreadcrumbSpan>
                        </BreadcrumbItem>
                    ) : null}
                </Breadcrumbs>
            </div>
            {/* Ensure the container is `relative` so that any focus in this container remains trapped within it and doesn't cause the entire document to shift and offset */}
            <div className="scrollbar relative flex flex-1 flex-col overflow-y-auto">
                <React.Suspense
                    fallback={
                        <div className="flex h-full w-full flex-col justify-start p-2">
                            <CommentBoxSkeleton />
                        </div>
                    }
                >
                    <div className="flex w-full flex-1 flex-col gap-2.5 bg-base">
                        {openedComment ? (
                            <CommentCard
                                space={space}
                                changeRequest={changeRequest}
                                comment={openedComment}
                                showContextLink={showContextLink}
                                isSelected
                                className="rounded-xl"
                            />
                        ) : (
                            <EmptyState>
                                <EmptyStateIcon>
                                    <Icon.Comment />
                                </EmptyStateIcon>
                                <Heading>Comment not found</Heading>
                                <Description>
                                    The comment you are looking for could not be found.
                                </Description>
                            </EmptyState>
                        )}
                    </div>
                </React.Suspense>
            </div>
        </>
    );
}

function CommentsEmptyState(props: {
    selectedTab: CommentsSidePanelTabs;
    isInChangeRequest: boolean;
}): React.ReactElement {
    const { selectedTab, isInChangeRequest } = props;

    const title = (() => {
        if (selectedTab === CommentsSidePanelTabs.Page) {
            return 'No comments on this page';
        }

        if (selectedTab === CommentsSidePanelTabs.All) {
            if (isInChangeRequest) {
                return 'No comments in this change request';
            }

            return 'No comments in this space';
        }

        assertNever(selectedTab);
    })();

    return (
        <EmptyState>
            <EmptyStateIcon>
                <Icon.Comment />
            </EmptyStateIcon>
            <Heading>{title}</Heading>
            <Description>{isInChangeRequest ? copy.cr : copy.space}</Description>
        </EmptyState>
    );
}

function CommentsList(props: {
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    comments: Array<api.Comment>;
    next: (() => void | Promise<void>) | undefined;
    previous: (() => void | Promise<void>) | undefined;
    selectedTab: CommentsSidePanelTabs;
    onOpenComment: (comment: api.Comment) => void;
}): React.ReactElement {
    const {
        space,
        changeRequest,
        comments: commentsList,
        next,
        previous,
        onOpenComment,
        selectedTab,
    } = props;

    const hiveClient = useHiveClient();
    const { revision } = useHiveRevision();

    // Sort the comments so the ones on deleted nodes/pages are always at the bottom of the list
    const comments = commentsList.slice().sort((aComment, bComment) => {
        const aIsOnDeletedNodeOrPage = isCommentOnDeletedNodeOrPage(aComment, revision, hiveClient);
        const bIsOnDeletedNodeOrPage = isCommentOnDeletedNodeOrPage(bComment, revision, hiveClient);

        // If both comments are on deleted nodes or pages, sort by postedAt
        if (aIsOnDeletedNodeOrPage && bIsOnDeletedNodeOrPage) {
            return new Date(bComment.postedAt).getTime() - new Date(aComment.postedAt).getTime();
        }

        // If only one comment is on a deleted node or page, sort it to the bottom
        if (aIsOnDeletedNodeOrPage) {
            return 1;
        }

        if (bIsOnDeletedNodeOrPage) {
            return -1;
        }

        return 0;
    });

    return (
        <div className="flex flex-1 flex-col py-2 pr-2 pl-2.5">
            {comments.map((comment) => {
                return (
                    <APIErrorBoundary
                        fallback={{
                            404: (
                                <Tooltip label="Comment not found">
                                    <CommentBoxSkeleton />
                                </Tooltip>
                            ),
                        }}
                        key={comment.id}
                    >
                        <CommentCard
                            space={space}
                            changeRequest={changeRequest}
                            comment={comment}
                            showContextLink={selectedTab === CommentsSidePanelTabs.All}
                            isSelected={false}
                            onPress={() => onOpenComment(comment)}
                            className="rounded-xl hover:bg-side"
                        />
                    </APIErrorBoundary>
                );
            })}

            {next || previous ? (
                <Pagination className="px-5 pb-5" previous={previous} next={next} />
            ) : null}
        </div>
    );
}
